<template>
  <div class="d-flex flex-column">
    <MultipleSelect
      class="white mb-4"
      itemValue="id"
      hide-details
      :loading="isLoadingCountries"
      :maxChipsShown="2"
      :value="filters.countryCodes"
      :options="countryCodeOptions"
      :showSelectAll="false"
      :label="$t('common.country_other')"
      @change="handleFilterChange($event, 'countryCodes')"
    />
    <MultipleSelect
      class="white mb-4"
      data-testid="peopleFilters-multipleSelect-sites"
      hide-details
      itemValue="id"
      :cacheItems="false"
      :label="$t('common.site_other')"
      :maxChipsShown="2"
      :options="sites"
      :showSelectAll="false"
      :value="filters.sites"
      @change="handleFilterChange($event, 'sites')"
    />
    <MultipleSelect
      class="white mb-4"
      data-testid="peopleFilters-multipleSelect-siteGroups"
      hide-details
      itemValue="id"
      :cacheItems="false"
      :label="$t('common.site_group_other')"
      :maxChipsShown="2"
      :options="siteGroups"
      :showSelectAll="false"
      :value="filters.siteGroups"
      @change="handleFilterChange($event, 'siteGroups')"
    />
    <MultipleSelect
      class="white mb-4"
      data-testid="peopleFilters-multipleSelect-tags"
      hide-details
      itemValue="id"
      :cacheItems="false"
      :label="$t('common.tag_other')"
      :maxChipsShown="2"
      :options="tags"
      :showSelectAll="false"
      :value="filters.tags"
      @change="handleFilterChange($event, 'tags')"
    />
    <MultipleSelect
      class="white mb-4"
      data-testid="peopleFilters-multipleSelect-tagGroups"
      hide-details
      itemValue="id"
      :cacheItems="false"
      :label="$t('common.tag_group_other')"
      :maxChipsShown="2"
      :options="tagGroups"
      :showSelectAll="false"
      :value="filters.tagGroups"
      @change="handleFilterChange($event, 'tagGroups')"
    />
    <v-autocomplete
      data-private
      dense
      clearable
      outlined
      hide-details
      return-object
      class="white mb-4"
      item-value="name"
      item-text="name"
      :menu-props="{ offsetY: true }"
      :items="availableStatus"
      :label="$t('common.status')"
      :placeholder="$t('menu.selection.select_an_option')"
      @change="handleStatusChange"
    />
    <v-autocomplete
      data-private
      dense
      clearable
      outlined
      return-object
      hide-details
      class="white mb-4"
      item-value="name"
      item-text="name"
      :menu-props="{ offsetY: true }"
      :items="availablePermissions"
      :label="$t('organization_users.permission_other')"
      :placeholder="$t('menu.selection.select_an_option')"
      :value="filters.permission"
      @change="handlePermissionChange"
    />
    <v-autocomplete
      data-private
      dense
      clearable
      outlined
      hide-details
      return-object
      class="white"
      item-text="name"
      item-value="name"
      :menu-props="{ offsetY: true }"
      :items="availableRoles"
      :label="$t('common.admin_role_other')"
      :placeholder="$t('menu.selection.select_an_option')"
      :value="filters.role"
      @change="handleRoleChange"
    />
  </div>
</template>
<script>
import { mapGetters } from 'vuex'

import api from '@/api'
import MultipleSelect from '@/components/Core/MultipleSelect.vue'

export default {
  name: 'PeopleFilters',

  components: {
    MultipleSelect,
  },

  props: {
    organizationId: {
      type: String,
      required: true,
    },
    organizationStructure: {
      type: Object,
      default: () => ({}),
    },
    filters: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      isLoadingCountries: false,
      countryCodes: [],
      availableStatus: [
        {
          value: 'joined',
          name: this.$t('people.status.joined'),
          key: 'status',
        },
        {
          value: 'pending',
          name: this.$t('people.status.pending'),
          key: 'status',
        },
        {
          value: 'created',
          name: this.$t('people.status.created'),
          key: 'status',
        },
        {
          value: true,
          name: this.$t('people.status.deactivated'),
          key: 'isDeleted',
        },
      ],
      availablePermissions: [
        {
          value: true,
          name: this.$t('common.roles.coach_one'),
          key: 'isCoach',
        },
        {
          value: false,
          name: this.$t('common.roles.user'),
          key: 'isCoach',
        },
        {
          value: true,
          name: this.$t('common.roles.impact_validator'),
          key: 'canDoFinancialValidation',
        },
        {
          value: true,
          name: this.$t('common.roles.routine_manager'),
          key: 'isRoutineManager',
        },
      ],
      availableRoles: [
        {
          value: true,
          name: this.$t('common.roles.organization_admin'),
          key: 'isOrganizationAdmin',
        },
        {
          value: true,
          name: this.$t('common.roles.site_admin'),
          key: 'isSiteAdmin',
        },
      ],
    }
  },

  computed: {
    ...mapGetters([
      'organizationSites',
      'organizationSiteGroups',
      'organizationTags',
      'organizationTagGroups',
    ]),

    isCurrentOrganization() {
      return this.organizationId === this.$store.getters.organizationId
    },

    sites() {
      return this.isCurrentOrganization
        ? this.organizationSites
        : this.organizationStructure?.sites ?? []
    },

    siteGroups() {
      return this.isCurrentOrganization
        ? this.organizationSiteGroups
        : this.organizationStructure?.siteGroups ?? []
    },

    tags() {
      return this.isCurrentOrganization
        ? this.organizationTags
        : this.organizationStructure?.tags ?? []
    },

    tagGroups() {
      return this.isCurrentOrganization
        ? this.organizationTagGroups
        : this.organizationStructure?.tagGroups ?? []
    },

    countryCodeOptions() {
      return (
        this.$constants.countries
          .map?.(country => {
            return {
              ...country,
              id: country.code,
              name: this.$t(`countries.${country.code}`),
            }
          })
          .filter?.(country =>
            this.countryCodes?.some?.(
              countryCode => countryCode === country.code
            )
          ) ?? []
      )
    },
  },

  created() {
    this.fetchCountries()
  },

  methods: {
    async fetchCountries() {
      try {
        this.isLoadingCountries = true
        const response = await api.country.getAllCountryCodes(
          this.organizationId
        )

        this.countryCodes = response.data
      } catch (error) {
        this.handleRequestError(error)
      } finally {
        this.isLoadingCountries = false
      }
    },

    handlePermissionChange(permission) {
      delete this.filters.isCoach
      delete this.filters.canDoFinancialValidation
      if (!permission?.key) this.$emit('change', this.filters)
      else this.handleFilterChange(permission.value, permission.key)
    },

    handleRoleChange(role) {
      delete this.filters.isOrganizationAdmin
      delete this.filters.isSiteAdmin
      if (!role?.key) this.$emit('change', this.filters)
      else this.handleFilterChange(role.value, role.key)
    },

    handleStatusChange(status) {
      delete this.filters.status
      delete this.filters.isDeleted
      if (!status?.key) this.$emit('change', this.filters)
      else this.handleFilterChange(status.value, status.key)
    },

    handleFilterChange(value, key) {
      if (key === 'countryCodes') this.clearAndFetchSites()
      this.filters[key] = value
      this.$emit('change', this.filters)
    },

    clearAndFetchSites() {
      this.filters.sites = []
    },
  },
}
</script>
